import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import EmailPhoneInput, {
  INPUT_TYPE,
} from '../../../../../../../components/global/EmailPhoneInput';
import Button from '../../../../../../../components/global/Button';
import Alert from '../../../../../../../components/global/Alert';
import { isValidEmail } from '../../../../../../../util/email';
import { isValidPhone } from '../../../../../../../util/phone';
import { CONTACT_CIRCLE_SAVE_MODAL } from '../../../../../../../components/global/ContactCircleSaveModal';
import CirclesIcon from '../../../../../../../components/icons/CirclesIcon';
import ContactCircleTile from '../../ShutterbugShare/components/ContactCircleTile';
import useCircles from '../../../../../views/Account/ContactCircles/hooks/useCircles';
import useOpenModal from '../../../../../../../hooks/useOpenModal';

const propTypes = {
  title: PropTypes.string,
  helperText: PropTypes.string,
  sendInvites: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  circlesEnabled: PropTypes.bool,
  inputIsPhoneDefault: PropTypes.bool,
  allowInputTypeSelection: PropTypes.bool,
};

const InvitationForm = ({
  title,
  helperText,
  sendInvites,
  onSuccess,
  submitting,
  circlesEnabled = false,
  inputIsPhoneDefault = false,
  allowInputTypeSelection = true,
}) => {
  const [input, setInput] = useState('');
  const [inputIsPhone, setInputIsPhone] = useState(inputIsPhoneDefault);
  const [invitees, setInvitees] = useState([]);
  const [errors, setErrors] = useState([]);
  const [circleInvitees, setCircleInvitees] = useState([]);
  const { circles } = useCircles();
  const openModal = useOpenModal();

  const onToggleCircle = circle => {
    if (circleInvitees.includes(circle.id)) {
      setCircleInvitees(
        circleInvitees.filter(circleId => circleId !== circle.id),
      );
    } else {
      setCircleInvitees([...circleInvitees, circle.id]);
    }
  };

  const getCircleContactsTotal = () => {
    if (circleInvitees.length === 0) {
      return 0;
    }

    return circles
      .filter(({ id }) => circleInvitees.includes(id))
      .reduce((sum, circle) => sum + circle.contacts?.total ?? 0, 0);
  };

  const onAddInvitee = () => {
    setErrors([]);

    if (!inputIsPhone && !isValidEmail(input)) {
      setErrors([`${input} is not a valid email address.`]);
      return;
    }
    if (inputIsPhone && !isValidPhone(input)) {
      setErrors([`${input} is not a valid phone number.`]);
      return;
    }

    if (!inputIsPhone && invitees.find(invitee => invitee.email === input)) {
      setErrors([`${input} is already added to the invitee list.`]);
      return;
    }

    if (
      inputIsPhone &&
      invitees.find(invitee => invitee.phoneNumber === input)
    ) {
      setErrors([`${input} is already added to the invitee list.`]);
      return;
    }

    setInvitees([
      ...invitees,
      {
        email: !inputIsPhone ? input : undefined,
        phoneNumber: inputIsPhone ? input : undefined,
      },
    ]);

    setInput('');
  };

  const onRemoveInvitee = invitee => {
    setInvitees(invitees.filter(activeInvitee => activeInvitee !== invitee));
  };

  const onSendInvites = async () => {
    const sendErrors = await sendInvites(
      invitees,
      input,
      inputIsPhone,
      circleInvitees,
    );

    if (sendErrors?.length === 0) {
      onSuccess();
    } else {
      setErrors(sendErrors);
    }
  };

  return (
    <form className="mt-2">
      {title && <h4 className="mb-1">{title}</h4>}
      <EmailPhoneInput
        id="invitee-phone-or-email"
        name="phone-or-email"
        className="mb-0"
        placeholder={helperText ?? "Type an email and press 'Enter'"}
        value={input}
        onChange={(value, isPhone) => {
          setInput(value);
          setInputIsPhone(isPhone);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            onAddInvitee();
            event.stopPropagation();
            event.preventDefault();
          }
        }}
        defaultInputType={inputIsPhoneDefault ? undefined : INPUT_TYPE.EMAIL}
        dropdownDisabled={!allowInputTypeSelection}
        disabled={submitting}
      />
      {helperText && inputIsPhoneDefault && (
        <p className="text-xs text-muted my-1">{helperText}</p>
      )}

      <div className="shutterbug-share-invitee-list">
        {invitees.map(invitee => (
          <Button
            disabled={submitting}
            key={invitee.email ?? invitee.phoneNumber}
            btnStyle="reset"
            className="shutterbug-share-invitee"
            onClick={() => onRemoveInvitee(invitee)}
          >
            {invitee.email ?? invitee.phoneNumber} &times;
          </Button>
        ))}
      </div>

      {circlesEnabled && (
        <Fragment>
          <h4 className="mb-1 w-100 d-flex justify-content-between">
            Invite a circle
            {(circles?.length ?? 0) > 0 && (
              <Button
                btnStyle="link"
                className="d-flex align-items-center text-bold"
                onClick={() => openModal(CONTACT_CIRCLE_SAVE_MODAL)}
              >
                <CirclesIcon size={16} className="mr-1" color="#0d84fe" />
                Create circle
              </Button>
            )}
          </h4>
          <div className="share-contact-circles">
            {(circles ?? []).map(circle => (
              <ContactCircleTile
                key={circle.id}
                circle={circle}
                onToggleCircle={() => onToggleCircle(circle)}
                isSelected={circleInvitees.includes(circle.id)}
              />
            ))}
            {(circles?.length ?? 0) === 0 && (
              <Button
                btnStyle="info"
                btnBlock
                className="d-flex align-items-center text-primary text-bold"
                onClick={() => openModal(CONTACT_CIRCLE_SAVE_MODAL)}
              >
                <CirclesIcon size={16} className="mr-1" color="#0d84fe" />{' '}
                Create a circle
              </Button>
            )}
          </div>
        </Fragment>
      )}

      {errors.length > 0 && (
        <Alert
          type="danger"
          className="mt-2"
          dismissible
          onDismiss={() => setErrors([])}
        >
          <p>We found some errors with your data:</p>
          <ul>
            {errors.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {(invitees.length > 0 || circleInvitees.length > 0) && (
        <Button
          btnBlock
          type="submit"
          disabled={submitting}
          onClick={onSendInvites}
          className="mt-4 text-sm"
        >
          Share with{' '}
          {pluralize(
            'person',
            invitees.length + getCircleContactsTotal(),
            true,
          )}
        </Button>
      )}
    </form>
  );
};

InvitationForm.propTypes = propTypes;

export default InvitationForm;
